import {
  AlertTriangle,
  Award,
  Calendar,
  ClipboardCheck,
  Copy,
  FileText,
  Gavel,
  IdCard,
  Key,
  Building2,
  LayoutDashboard,
  Mail,
  QrCode,
  School,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Trophy,
  UserPlus,
  Users,
  UserCircle,
} from "lucide-react";
import { UserRole } from "@comp-sys/shared";

export interface NavItem {
  href: string;
  icon: typeof LayoutDashboard;
  labelKey: string; // i18n key like "nav.dashboard"
  roles?: UserRole[]; // If undefined, visible to all roles
  children?: NavItem[];
}

const SA = UserRole.SYSTEM_ADMIN;
const SRA = UserRole.SUPER_ADMIN;
const ADMIN = UserRole.ADMIN;
const CM = UserRole.COMPETITION_MANAGER;
const CONTESTANT = UserRole.CONTESTANT;
const JUDGE = UserRole.JUDGE;
const COACH = UserRole.COACH;
const VOLUNTEER = UserRole.VOLUNTEER;
const SCHOOL = UserRole.SCHOOL;
const INSTITUTION = UserRole.INSTITUTION;

const ADMIN_ROLES = [SA, SRA, ADMIN];
const ALL_ROLES_EXCEPT_SCHOOL_INST = [SA, SRA, ADMIN, CM, CONTESTANT, JUDGE, COACH, VOLUNTEER];

/**
 * Navigation items ordered by logical workflow:
 * Dashboard → People → Judge Management → Organizations → Events → Competitions → Scoring → System
 *
 * Items with `children` render as collapsible groups in the sidebar.
 * Children inherit the parent's visibility but can further restrict via their own `roles`.
 */
export const NAV_ITEMS: NavItem[] = [
  // Dashboard - visible to all except SCHOOL and INSTITUTION
  { href: "/dashboard", icon: LayoutDashboard, labelKey: "dashboard", roles: ALL_ROLES_EXCEPT_SCHOOL_INST },

  // === Grouped items (SA/SRA optimization) ===

  // People Management - admin only
  {
    href: "/dashboard/users",
    icon: Users,
    labelKey: "people",
    roles: ADMIN_ROLES,
    children: [
      { href: "/dashboard/users", icon: Users, labelKey: "users", roles: ADMIN_ROLES },
      { href: "/dashboard/admin/profiles", icon: UserCircle, labelKey: "adminProfiles", roles: ADMIN_ROLES },
      { href: "/dashboard/admin/profile-issues", icon: AlertTriangle, labelKey: "profileIssues", roles: ADMIN_ROLES },
    ],
  },

  // Profiles - visible to all except SYSTEM_ADMIN and SUPER_ADMIN (they use admin profiles)
  {
    href: "/dashboard/profiles",
    icon: IdCard,
    labelKey: "profiles",
    roles: [ADMIN, CONTESTANT, JUDGE, COACH, VOLUNTEER],
  },

  // Judge Management - admin only (global judge management)
  {
    href: "/dashboard/admin/judges",
    icon: Gavel,
    labelKey: "judgeManagement",
    roles: ADMIN_ROLES,
    children: [
      { href: "/dashboard/admin/judges", icon: Gavel, labelKey: "judges", roles: ADMIN_ROLES },
      { href: "/dashboard/admin/judge-cert/questions", icon: ShieldCheck, labelKey: "judgeCertQuestions", roles: [SA, SRA] },
      { href: "/dashboard/admin/judge-cert/configs", icon: ShieldCheck, labelKey: "judgeCertConfigs", roles: [SA, SRA] },
      { href: "/dashboard/admin/judge-certificate-batches", icon: QrCode, labelKey: "judgeCertificateBatches", roles: ADMIN_ROLES },
      { href: "/dashboard/admin/judge-certificates", icon: QrCode, labelKey: "judgeCertificatesAll", roles: ADMIN_ROLES },
    ],
  },

  // Organizations - admin only
  {
    href: "/dashboard/admin/schools",
    icon: School,
    labelKey: "organizations",
    roles: ADMIN_ROLES,
    children: [
      { href: "/dashboard/admin/schools", icon: School, labelKey: "schools", roles: ADMIN_ROLES },
      { href: "/dashboard/admin/institutions", icon: Building2, labelKey: "institutions", roles: ADMIN_ROLES },
    ],
  },

  // Duplicates - super admin and system admin only
  {
    href: "/dashboard/admin/duplicates",
    icon: Copy,
    labelKey: "duplicates",
    roles: [SA, SRA],
  },

  // Audit Center - super admin and system admin only
  {
    href: "/dashboard/admin/audit-center",
    icon: ShieldAlert,
    labelKey: "auditCenter",
    roles: [SA, SRA],
  },

  // Insurance Review - competition manager and admins
  {
    href: "/dashboard/admin/insurances",
    icon: ShieldCheck,
    labelKey: "insurances",
    roles: [SA, SRA, ADMIN, CM],
  },

  // Badge Templates - admin only
  {
    href: "/dashboard/admin/badge-templates",
    icon: FileText,
    labelKey: "badgeTemplates",
    roles: ADMIN_ROLES,
  },

  // === Ungrouped items ===

  // School pages — SCHOOL role only
  { href: "/dashboard/school", icon: LayoutDashboard, labelKey: "mySchool.overview", roles: [SCHOOL] },
  { href: "/dashboard/school/profiles", icon: IdCard, labelKey: "mySchool.profiles", roles: [SCHOOL] },
  { href: "/dashboard/school/enrollments", icon: Calendar, labelKey: "mySchool.enrollments", roles: [SCHOOL] },
  { href: "/dashboard/school/users", icon: Users, labelKey: "mySchool.users", roles: [SCHOOL] },
  { href: "/dashboard/school/awards", icon: Trophy, labelKey: "mySchool.awards", roles: [SCHOOL] },
  { href: "/dashboard/school/settings", icon: Settings, labelKey: "mySchool.settings", roles: [SCHOOL] },

  // Institution pages — INSTITUTION role only
  { href: "/dashboard/institution", icon: LayoutDashboard, labelKey: "myInstitution.overview", roles: [INSTITUTION] },
  { href: "/dashboard/institution/profiles", icon: IdCard, labelKey: "myInstitution.profiles", roles: [INSTITUTION] },
  { href: "/dashboard/institution/enrollments", icon: Calendar, labelKey: "myInstitution.enrollments", roles: [INSTITUTION] },
  { href: "/dashboard/institution/users", icon: Users, labelKey: "myInstitution.users", roles: [INSTITUTION] },
  { href: "/dashboard/institution/awards", icon: Trophy, labelKey: "myInstitution.awards", roles: [INSTITUTION] },
  { href: "/dashboard/institution/settings", icon: Settings, labelKey: "myInstitution.settings", roles: [INSTITUTION] },

  // Events - visible to all
  { href: "/dashboard/events", icon: Calendar, labelKey: "events" },

  // Invite Codes - admin only
  { href: "/dashboard/admin/invite-codes", icon: Key, labelKey: "inviteCodes", roles: ADMIN_ROLES },

  // Judge - judges, admins, and competition managers (CMs score their own competitions)
  {
    href: "/dashboard/judge",
    icon: ClipboardCheck,
    labelKey: "judge",
    roles: [SA, SRA, ADMIN, JUDGE, CM],
  },

  // Judge Registration - judges only
  { href: "/dashboard/judge-registration", icon: UserPlus, labelKey: "judgeRegistration", roles: [JUDGE] },
  { href: "/dashboard/judge-certification", icon: ShieldCheck, labelKey: "judgeCertification", roles: [JUDGE] },
  // My Judge Certificates - judges only (download own certificate image)
  { href: "/dashboard/judge/certificates", icon: Award, labelKey: "judgeMyCertificates", roles: [JUDGE] },

  // Preparation (participant view) - visible to contestants, judges, coaches, volunteers
  { href: "/dashboard/preparation", icon: ClipboardCheck, labelKey: "preparation", roles: [CONTESTANT, JUDGE, COACH, VOLUNTEER] },

  // My Competitions (participant view) - visible to contestants, judges, coaches, volunteers
  { href: "/dashboard/my-competitions", icon: Trophy, labelKey: "myCompetitionsParticipant", roles: [CONTESTANT, JUDGE, COACH, VOLUNTEER] },

  // Messages - visible to all authenticated users
  { href: "/dashboard/messages", icon: Mail, labelKey: "messages" },

  // Settings - system admin and super admin only
  {
    href: "/dashboard/settings",
    icon: Settings,
    labelKey: "settings",
    roles: [SA, SRA],
  },

  // Admin Messages - system admin and super admin only
  {
    href: "/dashboard/admin/messages",
    icon: Mail,
    labelKey: "adminMessages",
    roles: [SA, SRA],
  },
];
